// Strona pojedynczego produktu (wariant Wise - równoległy redesign, nie modyfikuje Product.tsx)

import React, { type SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Trans, useTranslation } from 'react-i18next';
import { useParams, useLocation } from 'react-router-dom';
import classnames from 'classnames';
import animateScrollTo from 'animated-scroll-to';
import { Grid } from '@mui/material';

import { useRWD } from 'hooks';
import {
  useGetProduct,
  useGetProductSubelementsTypes,
  useGetProductSubelementItems,
  useGetProductBreadcrumbs
} from 'api';
import { IProductsBreadcrumbsRequest } from 'api/types';
import { reduxActions, useDispatch, useSelector } from 'store';
import { Container, Link, Loader } from 'components/controls';
import { AddToCartButton, ProductSlider, Search, WiseBreadcrumbs } from 'components/containers';
import {
  WiseAccordion,
  WiseDetails,
  WisePrices,
  WiseGallery,
  WiseProductTabs,
  WiseAttributeList,
  WiseOthersAlsoBought,
  WiseLastViewed,
  WiseProductSubelements,
  WiseMobileBuyBar
} from './components';
import { generateProductPageScheme } from 'api/helpers';

import 'pure-react-carousel/dist/react-carousel.es.css';
import styles from 'theme/pages/wise/WiseProduct/WiseProduct.module.scss';

const WiseProduct = ({ id, h1, hasSeoTitle }: { id?: string; h1?: string | null; hasSeoTitle?: boolean }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.auth);

  const { isMobile, isDesktop } = useRWD();

  // referencja do paska nawigacji (sticky)
  const navWrapperRef = useRef<HTMLDivElement>(null);
  /** Punkt nad paskiem - gdy wyjedzie z widoku kontenera scrolla, pokazujemy mini-pasek (productInfoBar) */
  const navStickySentinelRef = useRef<HTMLDivElement>(null);

  // górna część PDP (zakładka „Produkt”)
  const productTopRef = useRef<HTMLDivElement>(null);

  // wstrzymanie synchronizacji activeTab z IO po kliknięciu zakładki / programowym scrollu
  const scrollSyncPausedUntilRef = useRef(0);
  const activeSubelementIdRef = useRef<number | null>(null);

  // referencja do sekcji pełny opis
  const fullDescriptionRef = useRef<HTMLDivElement>(null);

  // referencja do sekcji z technicznymi atrybutami
  const technicalAttributesRef = useRef<HTMLDivElement>(null);

  // referencja do sekcji z informacjami o dostawie
  const logisticAttributesRef = useRef<HTMLDivElement>(null);

  // referencja do sekcji z plikami
  const filesRef = useRef<HTMLDivElement>(null);

  // referencja do sekcji z załącznikami
  const attachmentsRef = useRef<HTMLDivElement>(null);

  // referencja do sekcji z subelementami
  const subelementsRef = useRef<HTMLDivElement>(null);

  // ID produktu (przekształcony na int)
  const { id: paramId } = useParams();
  const productId = useMemo(() => parseInt(paramId || id || ''), [id]);

  // ID kategorii lub fraza wyszukiwania potrzebne jako param w breadcrumbs
  const { state } = useLocation();
  const { categoryId, searchKeywords } = (state || {}) as IProductsBreadcrumbsRequest;

  // Aktualnie wybrany tab (główna nawigacja)
  const [activeTab, setActiveTab] = useState<string | number>('id');

  // Aktualnie wybrany typ subelementu
  const [activeSubelementId, setActiveSubelementId] = useState<number | null>(null);
  activeSubelementIdRef.current = activeSubelementId;

  // Lista rozwiniętych sekcji
  const [activeSections, setActiveSections] = useState<string[]>(['description_full']);

  // czy jest nawigacja w trakcie sticky
  const [isStickyNav, setIsStickyNav] = useState(false);

  // Pobranie produktu
  const { data: productData, isLoading: isProductLoading } = useGetProduct(productId, {
    enabled: !!productId
  });

  // Pobranie typów subelementów
  const { data: productSubelementsTypesData } = useGetProductSubelementsTypes(
    productId,
    {
      page: 1,
      limit: 999
    },
    {
      enabled: !!productId,
      onSuccess: (data) => {
        // Ustawienie pierwszego typ subelementu jako wybrany
        setActiveSubelementId(data.items[0]?.id);
      }
    }
  );

  // Pobranie listy produktów po zmianie/wybraniu typu subelementu
  const { data: productSubelementItemsData, isLoading: isProductSubelementItemsLoading } =
    useGetProductSubelementItems(
      productId,
      activeSubelementId || 0,
      {
        page: 1,
        limit: 999
      },
      {
        enabled: !!productId && !!activeSubelementId
      }
    );

  // Pobranie listy breadcrumbs'ów danego produktu
  const { data: productBreadcrumbsData } = useGetProductBreadcrumbs(
    productId,
    {
      ...(categoryId ? { categoryId } : {}),
      ...(searchKeywords ? { searchKeywords } : {})
    },
    {
      enabled: !!productId
    }
  );

  // Ustawienie breadcrums'ów do wyświetlenia JCZ: w jakim momencie ? Po załadowaniu danych ?
  useEffect(() => {
    dispatch(
      reduxActions.setBreadcrumbs(
        productBreadcrumbsData
          ? productBreadcrumbsData.items.map((item) => ({
              name: item.name,
              path: item.url_link ? `/${item.url_link}` : undefined
            }))
          : [
              {
                name: 'Produkty',
                path: '/products'
              },
              {
                name: productData?.product_basic_full_info?.title || 'Produkt'
              }
            ]
      )
    );
  }, [productData, productBreadcrumbsData]);

  // Mini-pasek produktu: gdy sentinel nad nawigacją nie jest już widoczny w .scrollableContent
  useEffect(() => {
    const sentinel = navStickySentinelRef.current;
    const root = document.getElementsByClassName('scrollableContent')[0] as HTMLElement | undefined;
    if (!sentinel || !root) {
      return;
    }

    const observer = new IntersectionObserver(([entry]) => setIsStickyNav(!entry.isIntersecting), {
      root,
      threshold: 0
    });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [productData]);

  // Elementy głównej nawigacji
  const navTabs: { property: string | number; label: string; collapse?: boolean; tab?: boolean }[] = useMemo(() => {
    const tabs: { property: string | number; label: string; collapse?: boolean; tab?: boolean }[] = [
      { property: 'id', label: t('Produkt') },
      { property: 'description_full', label: t('Pełny opis'), collapse: true }
    ];

    if (productData?.product_technical_attributes_full_info?.attributes?.length) {
      tabs.push({
        property: 'technical_attributes',
        label: t('Dane techniczne'),
        collapse: true
      });
    }

    if (productData?.product_logistics_attributes_full_info?.attributes?.length) {
      tabs.push({
        property: 'logistic_attributes',
        label: t('Dane logistyczne'),
        collapse: true
      });
    }

    if (productData?.product_attachment_full_info?.files?.length) {
      tabs.push({
        property: 'attachments',
        label: t('Załączniki'),
        collapse: true
      });
    }

    if (productSubelementsTypesData?.items?.length) {
      tabs.push(
        ...productSubelementsTypesData.items.map((subelement) => ({
          property: subelement.id,
          label: subelement.name,
          tab: true
        }))
      );
    }

    return tabs;
  }, [productData, productSubelementsTypesData]);

  // pobranie referencji do sekcji na podstawie nazwy pola w produkcie
  const getRefElement = (elementName: string) =>
    elementName === 'id'
      ? productTopRef
      : elementName === 'description_full'
      ? fullDescriptionRef
      : elementName === 'technical_attributes'
      ? technicalAttributesRef
      : elementName === 'logistic_attributes'
      ? logisticAttributesRef
      : elementName === 'files'
      ? filesRef
      : elementName === 'attachments'
      ? attachmentsRef
      : elementName === 'subelements'
      ? subelementsRef
      : null;

  // funkcja odpowiedzialna za przeskrolowanie strony do odpowiedniej sekcji
  const scrollToElement = (elementName: string) => {
    scrollSyncPausedUntilRef.current = performance.now() + 480;

    const element = getRefElement(elementName)?.current;

    element
      ? animateScrollTo(element, {
          elementToScroll: document.getElementsByClassName('scrollableContent')[0],
          verticalOffset: isStickyNav ? -50 : 25
        })
      : animateScrollTo(0, {
          elementToScroll: document.getElementsByClassName('scrollableContent')[0]
        });

    setActiveSections([...activeSections, elementName]);
  };

  useEffect(() => {
    if (!productData) {
      return;
    }

    const root = document.getElementsByClassName('scrollableContent')[0] as HTMLElement | undefined;
    if (!root) {
      return;
    }

    const buildSections = (): { el: HTMLElement; resolveTab: () => string | number }[] => {
      const list: { el: HTMLElement; resolveTab: () => string | number }[] = [];
      if (productTopRef.current) {
        list.push({ el: productTopRef.current, resolveTab: () => 'id' });
      }
      navTabs.forEach((tab) => {
        if (!tab.collapse || typeof tab.property !== 'string') {
          return;
        }
        const el = getRefElement(tab.property)?.current;
        if (el) {
          const id = tab.property;
          list.push({ el, resolveTab: () => id });
        }
      });
      if (productSubelementsTypesData?.items?.length && subelementsRef.current) {
        list.push({
          el: subelementsRef.current,
          resolveTab: () =>
            activeSubelementIdRef.current ?? productSubelementsTypesData.items[0]?.id ?? 'id'
        });
      }
      return list;
    };

    const getActivationLineY = () => {
      const rootRect = root.getBoundingClientRect();
      const inset = isStickyNav ? 100 : 72;
      return rootRect.top + inset;
    };

    const updateActiveTab = () => {
      if (performance.now() < scrollSyncPausedUntilRef.current) {
        return;
      }

      const sections = buildSections();
      if (!sections.length) {
        return;
      }

      const lineY = getActivationLineY();
      let next: string | number = 'id';

      for (const { el, resolveTab } of sections) {
        const top = el.getBoundingClientRect().top;
        if (top <= lineY) {
          next = resolveTab();
        }
      }

      setActiveTab((prev) => (prev === next ? prev : next));
    };

    let raf = 0;
    const onScrollOrResize = () => {
      if (raf) {
        return;
      }
      raf = requestAnimationFrame(() => {
        raf = 0;
        updateActiveTab();
      });
    };

    updateActiveTab();
    root.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);

    return () => {
      root.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      if (raf) {
        cancelAnimationFrame(raf);
      }
    };
  }, [
    productData,
    navTabs,
    activeSections,
    isStickyNav,
    productSubelementsTypesData?.items?.length
  ]);

  // Generowanie JSON-LD schema dla produktu
  const productPageScheme = useMemo(() => {
    if (!productData) return null;
    return generateProductPageScheme(productData, window.location.href);
  }, [productData]);

  return (
    <div
      className={classnames(styles.componentWrapper, 'StylePath-Pages-WiseProduct')}
      itemScope
      itemType="http://schema.org/Product">
      {productData && (
        <Helmet>
          {!hasSeoTitle && <title>{productData?.product_basic_full_info?.title}</title>}
          <link rel="canonical" href={window.location.href} />
          {productPageScheme && (
            productPageScheme.map((scheme, index) => (
              <script key={index} type="application/ld+json">
                {JSON.stringify(scheme)}
              </script>
            ))
          )}
        </Helmet>
      )}

      <WiseBreadcrumbs />

      {isProductLoading && <Loader />}

      {productData && (
        <>
          <div
            ref={productTopRef}
            className={styles.productDetailsWrapper}
            data-wise-nav-tab="id">
            <Container>
              {/* Mobile: nagłówek produktu (h1 + INDEX/Producent/Kategoria) PRZED galerią.
                  Duplikat z LegacyDetails jest ukrywany na mobile przez WiseDetails.module.scss. */}
              <div className={styles.mobileProductHeader}>
                <h1 className={styles.mobileProductTitle}>
                  {h1 || productData?.product_basic_full_info?.title}
                </h1>
                <ul className={styles.mobileProductMeta}>
                  <li>
                    <Trans>INDEX</Trans>:{' '}
                    <strong>{productData?.product_basic_full_info?.index}</strong>
                  </li>
                  {productData?.product_basic_full_info?.producer_name && (
                    <li>
                      <Trans>Producent</Trans>:{' '}
                      <strong>{productData?.product_basic_full_info?.producer_name}</strong>
                    </li>
                  )}
                  <li>
                    <Trans>Kategoria</Trans>:{' '}
                    <strong>
                      <Link to={`/${productData?.product_basic_full_info?.category_url}`}>
                        {productData?.product_basic_full_info?.category_name}
                      </Link>
                    </strong>
                  </li>
                </ul>
              </div>

              <Grid container className={styles.productDetailsGrid}>
                <Grid item md={3} xs={12} className={styles.productDetailsTopCol}>
                  <WiseGallery
                    images={productData?.product_image_formatted_full_info?.images || []}
                    labels={productData?.product_basic_full_info?.labels || []}
                  />
                </Grid>

                <Grid item md={5} xs={12} className={styles.productDetailsTopCol}>
                  <WiseDetails
                    product={productData}
                    scrollToDescription={() => {
                      setActiveTab('description_full');
                      scrollToElement('description_full');
                    }}
                    h1={h1}
                  />
                </Grid>

                <Grid item md={4} xs={12} className={styles.productDetailsTopCol}>
                  <WisePrices product={productData} />
                </Grid>
              </Grid>
            </Container>
          </div>

          <div ref={navStickySentinelRef} className={styles.navStickySentinel} aria-hidden />
          <div className={styles.productNavWrapper} ref={navWrapperRef}>
            {isStickyNav && (
              <div className={styles.productInfoBarWrapper}>
                <Container>
                  <div className={styles.productInfoBar}>
                    <div className={styles.info}>
                      <div
                        className={styles.image}
                        style={{
                          backgroundImage: `url('${productData?.product_image_formatted_full_info?.images?.[0]?.thumb ?? ''}')`
                        }}
                      />
                      <span itemProp="name" className={styles.title}>
                        {productData?.product_basic_full_info?.title}
                      </span>
                    </div>

                    <div className="addToCartSmallButtonWrapper">
                      <AddToCartButton
                        quantity={1}
                        unitId={productData?.product_price_full_info?.prices?.[0]?.unit_id || 1}
                        productId={productData.id}
                        disabled={profile?.role === 'ROLE_OPEN_PROFILE'}
                      />
                    </div>
                  </div>
                </Container>
              </div>
            )}

            <Container>
              <WiseProductTabs
                items={navTabs.map((section) => ({
                  value: section.property,
                  label: section.label
                }))}
                value={activeTab}
                onChange={(event: SyntheticEvent, property: string | number) => {
                  setActiveTab(property);
                  typeof property === 'number'
                    ? (setActiveSubelementId(property), scrollToElement('subelements'))
                    : (setActiveSections([...activeSections, property]), scrollToElement(property));
                }}
              />
            </Container>
          </div>

          <div className={styles.productExtendedWrapper}>
            {navTabs
              .filter((item) => item.collapse)
              .map((item) => (
                <div
                  ref={getRefElement(typeof item.property === 'string' ? item.property : '')}
                  data-wise-nav-tab={String(item.property)}
                  style={{ marginBottom: '24px' }}
                  key={item.property}>
                  <WiseAccordion
                    sectionId={item.property.toString()}
                    title={item.label}
                    expanded={activeSections.includes(item.property.toString())}
                    onExpandedChange={(isExpanded: boolean) => {
                      const id = item.property.toString();
                      if (isExpanded) {
                        setActiveSections((prev) => (prev.includes(id) ? prev : [...prev, id]));
                        setActiveTab(item.property);
                      } else {
                        setActiveSections((prev) => prev.filter((x) => x !== id));
                      }
                    }}>
                    {item.property === 'description_full' &&
                      productData?.product_basic_full_info?.description && (
                        <div
                          className={styles.fullDescription}
                          dangerouslySetInnerHTML={{
                            __html: productData?.product_basic_full_info?.description
                          }}
                        />
                      )}
                    {item.property === 'technical_attributes' && (
                      <WiseAttributeList
                        attributes={productData?.product_technical_attributes_full_info?.attributes || []}
                      />
                    )}
                    {item.property === 'logistic_attributes' && (
                      <WiseAttributeList
                        attributes={productData?.product_logistics_attributes_full_info?.attributes || []}
                      />
                    )}
                    {item.property === 'attachments' && (
                      <WiseAttributeList
                        attributes={productData?.product_attachment_full_info?.files || []}
                      />
                    )}
                  </WiseAccordion>
                </div>
              ))}
          </div>

          {productSubelementsTypesData && productSubelementsTypesData.items.length > 0 && (
            <div ref={subelementsRef} data-wise-nav-tab="subelements">
              <WiseProductSubelements productId={productId} />
            </div>
          )}

          <div className={styles.othersAlsoBoughtWrapper}>
            <Container>
              <WiseOthersAlsoBought
                productId={productId}
                showNavArrows={!isMobile}
              />
            </Container>
          </div>

          <div className={styles.lastViewedWrapper}>
            <Container>
              <WiseLastViewed productId={productId} showNavArrows={!isMobile} />
            </Container>
          </div>

          {/* Sticky mobile buy bar - szybkie dodanie do koszyka z modalem wyboru jednostki/ilości */}
          {isMobile && (
            <WiseMobileBuyBar
              productId={productData.id}
              prices={productData?.product_price_full_info?.prices || []}
              productName={h1 || productData?.product_basic_full_info?.title}
              productImage={productData?.product_image_formatted_full_info?.images?.[0]?.thumb}
            />
          )}
        </>
      )}
    </div>
  );
};

export default WiseProduct;
